import { ComponentProps, MouseEvent, ReactNode, useState } from 'react';
import styled, { css } from 'styled-components';
// eslint-disable-next-line import/no-namespace
import * as SS from 'styled-system';

import { Hide } from 'components/Hide';
import { Icon } from 'components/Icon';
import { Link as ReactRouterLink } from 'components/RouterLink';
import { colors, shadows } from 'theme';
import { ClickOutside } from 'utils/ClickOutside';

const navLinkCss = css<{ $blue?: boolean }>`
  font-size: inherit;
  text-decoration: none;
  white-space: nowrap;
  cursor: pointer;
  &:disabled {
    opacity: 1/4;
  }
  ${SS.fontWeight};

  color: ${(props) => (props.$blue ? colors.brandBlue : '#717375')};
`;

const NavLink = styled.a<{ $blue?: boolean }>`
  ${navLinkCss};
`;

const RouterNavLink = styled(ReactRouterLink)<{ $blue?: boolean }>`
  ${navLinkCss}
`;

const MenuList = styled.div<{ $up?: boolean }>`
  ${(props) =>
    props.$up &&
    css`
      display: flex;
      flex-direction: column-reverse;
    `};
`;
const StyledDropdownMenuNav = styled(NavLink)`
  display: flex;
  align-items: center;
  margin: 0;
  padding: 11px 12px;
  &:hover,
  &:focus {
    text-decoration: underline;
  }
`;

type HideOptions = {
  xs?: boolean;
  sm?: boolean;
  md?: boolean;
  lg?: boolean;
  xl?: boolean;
};

function DropdownMenuNav({
  open,
  up,
  children,
  hideArrow,
  ...props
}: {
  open: boolean;
  up?: boolean;
  children: ReactNode;
  hideArrow?: HideOptions;
} & ComponentProps<typeof StyledDropdownMenuNav>) {
  return (
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line react/jsx-props-no-spreading
    <StyledDropdownMenuNav {...props}>
      <div>{children}</div>
      {/* TODO: Fix this the next time the file is edited. */}
      {/* eslint-disable-next-line react/jsx-props-no-spreading */}
      <Hide {...hideArrow}>
        <div style={{ marginTop: -2, marginLeft: 5 }}>
          <Icon
            name={
              (open && !up) || (!open && up) ? 'chevron-up' : 'chevron-down'
            }
            size={9}
            initialColorName="lightContentGrey"
          />
        </div>
      </Hide>
    </StyledDropdownMenuNav>
  );
}

const menuItemCss = css`
  display: block;
  margin: 0;
  max-width: 220px;
  min-width: 100%;
  padding: 10px 12px;
  overflow: hidden;
  cursor: pointer;
  color: ${colors.lightContentGrey};
  text-overflow: ellipsis;
  text-decoration: none;
  font-size: inherit;
  white-space: nowrap;
  font-weight: normal;

  &:hover {
    background-color: #f2eeee;
    text-decoration: none;
    color: ${colors.mediumContentGrey};
  }
`;

export const PageHeaderDropdownMenuItem = styled(NavLink)`
  ${menuItemCss};
`;

export const DropdownMenuItemRouter = styled(RouterNavLink)`
  ${menuItemCss};
`;

const DropdownMenuContainer = styled.div<{ $up?: boolean; $open?: boolean }>`
  position: absolute;
  left: 0;
  z-index: 2000;
  overflow: hidden;
  box-shadow: ${shadows.highest};
  background-color: white;
  border-radius: 8px;
  transition: all 0.166s;
  ${(props) =>
    props.$open
      ? `
    opacity: 1;
    visibility: visible;
  `
      : `
    opacity: 0;
    visibility: hidden;
  `}
  ${(props) =>
    props.$up
      ? css`
          bottom: 0;
          ${PageHeaderDropdownMenuItem}, ${DropdownMenuItemRouter} {
            border-bottom: solid 1px ${colors.selectionGrey};
          }
        `
      : css`
          top: 0;
          ${PageHeaderDropdownMenuItem}, ${DropdownMenuItemRouter} {
            border-top: solid 1px ${colors.selectionGrey};
          }
        `};
`;
const StyledDropdownMenu = styled.div<{ $right?: boolean }>`
  position: relative;
  ${(props) =>
    props.$right &&
    css`
      margin-left: auto;
    `};
`;
type Props = {
  title: (arg0: boolean) => ReactNode;
  right?: boolean;
  up?: boolean;
  children:
    | ReactNode
    | ((data: { closeMenu: () => void; open: boolean }) => void);
  'data-qa-id': string;
  openQaId?: string;
  closeQaId?: string;
  dropdownQaId?: string;
  'aria-label'?: string;
  blue?: boolean;
  hideArrow?: HideOptions;
  onClick?: (open: boolean) => void;
  onClose?: (open: boolean) => void;
};

export function PageHeaderDropdownMenu({
  title,
  right,
  up,
  children,
  'data-qa-id': qaId,
  openQaId,
  closeQaId,
  dropdownQaId,
  'aria-label': ariaLabel,
  blue,
  hideArrow,
  onClick,
  onClose,
  ...props
}: Props) {
  const [open, setOpen] = useState(false);

  const toggleMenu = (e: MouseEvent) => {
    if (open && onClose) {
      onClose(!open);
    }

    setOpen(!open);
    e.preventDefault();
  };

  const closeMenu = () => {
    if (open && onClose) {
      onClose(false);
    }

    setOpen(false);
  };

  const dropdownNavClick = (e: MouseEvent) => {
    toggleMenu(e);

    if (onClick) {
      onClick(!open);
    }
  };

  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line react/function-component-definition, react/no-unstable-nested-components
  const DropdownNav = ({ menuState }: { menuState: string }) => (
    <DropdownMenuNav
      href="#"
      onClick={dropdownNavClick}
      open={open}
      up={up}
      data-qa-id={qaId ? `${qaId}-${menuState}` : undefined}
      aria-label={ariaLabel}
      $blue={blue}
      hideArrow={hideArrow}
    >
      {title(open)}
    </DropdownMenuNav>
  );

  return (
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line react/jsx-props-no-spreading
    <StyledDropdownMenu $right={right} {...props}>
      <DropdownNav menuState="open" />
      <DropdownMenuContainer $open={open} $up={up} data-qa-id={qaId}>
        <ClickOutside onClickOutside={closeMenu}>
          <MenuList $up={up}>
            <>
              <DropdownNav menuState="close" />

              {typeof children === 'function'
                ? children({ closeMenu, open })
                : children}
            </>
          </MenuList>
        </ClickOutside>
      </DropdownMenuContainer>
    </StyledDropdownMenu>
  );
}

export const PageHeaderDropdownMenuSection = styled.div`
  border-top: solid 1px ${colors.selectionGrey};
  width: 100%;
  padding: 9px 0;
`;

export const PageHeaderDropdownMenuSectionTitle = styled.div`
  color: #b0b4b9;
  font-size: inherit;
  font-style: italic;
  text-decoration: none;
  white-space: nowrap;
  display: block;
  margin: 0;
  padding: 6px 12px;
`;

export const DropdownMenuSectionItem = styled(PageHeaderDropdownMenuItem)`
  border: none;
  padding: 6px 12px;
`;

export const PageHeaderDropdownMenuSectionItemRouter = styled(
  DropdownMenuItemRouter,
)`
  border: none;
  padding: 6px 12px;
`;
